import type {
    GetInsightFamilyRequest,
    InsightFamily,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { AsyncState, UseAsyncActions } from "@react-hookz/web";
import { useAsyncAbortable } from "@react-hookz/web";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import type { InsightFamiliesFilters } from "../../utils/insightFamilies.utils";
import { useAsyncErrorStatusNotification } from "../notifications/useAsyncErrorStatusNotification";

export interface InsightFamilyActionProps {
    advertiserId?: string;
    marketplaceId?: string;
    familyInstanceId?: string;
}

export interface InsightFamilyActionResponse {
    insightFamily?: InsightFamily;
    filters?: InsightFamiliesFilters;
}

const emptyResponse: InsightFamilyActionResponse = {
    insightFamily: undefined,
    filters: undefined,
};

export const useGetInsightFamilyAction = ({
    advertiserId,
    marketplaceId,
    familyInstanceId,
}: InsightFamilyActionProps = {}): [
    AsyncState<InsightFamilyActionResponse>,
    UseAsyncActions<InsightFamilyActionResponse>,
] => {
    const request: GetInsightFamilyRequest = {
        advertiserId,
        marketplaceId,
        familyInstanceId,
    };

    const [insightsState, insightsAction] = useAsyncAbortable(async () => {
        const { insightFamily } = await (
            await getCdbApiClient()
        ).getInsightFamily(request);

        return {
            insightFamily,
            filters: generateFilterFromInsight(insightFamily!),
        } as InsightFamilyActionResponse;
    }, emptyResponse);

    useAsyncErrorStatusNotification(insightsState);

    return [insightsState, insightsAction];
};

const generateFilterFromInsight = (
    insight: InsightFamily,
): InsightFamiliesFilters => {
    const createFilterItemFromString = (...values: string[]) => {
        return {
            ...values.reduce(
                (acc, value) => ({
                    ...acc,
                    [value]: {
                        label: value,
                        value,
                    },
                }),
                {},
            ),
        };
    };
    return {
        FAMILY_BRAND: createFilterItemFromString(insight.brand!),
        FAMILY_BROWSE_NODE: createFilterItemFromString(insight.browseNode!),
        FAMILY_OBJECTIVE: createFilterItemFromString(...insight.objectives!),
    };
};
